import { loPublicSitePath } from '@/lib/lyrraone-custom-domain';
import { creatorFormationHref, isExternalUrl, resolveLoSiteLink } from '@/lib/lyrraone-site-links';
import { resolveFormationPlayHref, type FormationPlayMeta } from '@/lib/formation-play-url';
import type { FunnelStep, FunnelStepType, SalesFunnel } from '@/lib/sales-funnel';
import { funnelStepPublicPath, getNextFunnelStepPath, getSortedSteps } from '@/lib/sales-funnel';

export { funnelStepPublicPath } from '@/lib/sales-funnel';

export type LoBlockLinkKind =
  | 'funnel-action'
  | 'funnel-step'
  | 'formations-catalog'
  | 'formation'
  | 'external';

export interface LoBlockLinkTarget {
  kind: LoBlockLinkKind;
  /** funnel-step only */
  funnelStep?: FunnelStepType;
  /** formation only */
  formationId?: string;
  /** external only */
  url?: string;
}

export interface LoBlockLinkContext {
  mode: 'site' | 'funnel';
  siteSlug?: string | null;
  customDomainSlug?: string | null;
  funnelSlug?: string;
  funnelStepType?: FunnelStepType;
  /** Pages libres : CTA par défaut vers l'étape suivante */
  funnelNextStepHref?: string;
  /** Formation vendue par le funnel (page merci). */
  funnelOfferFormationId?: string;
  funnelOfferFlowId?: string;
  funnelOfferCreationMode?: 'simple' | 'advanced' | null;
  funnelOfferShortCodePublic?: string | null;
  formations?: Array<{
    id: string;
    title: string;
    funnelSlug?: string | null;
    flowId?: string | null;
    creationMode?: 'simple' | 'advanced' | null;
    shortCodePublic?: string | null;
  }>;
}

const FORMATION_PREFIX = '#formation:';
const FUNNEL_STEP_PREFIX = '#funnel-step:';

function formationMetaFromContext(
  formationId: string,
  ctx: LoBlockLinkContext,
): FormationPlayMeta | null {
  if (ctx.funnelOfferFormationId === formationId && ctx.funnelOfferFlowId) {
    return {
      flowId: ctx.funnelOfferFlowId,
      creationMode: ctx.funnelOfferCreationMode ?? null,
      shortCodePublic: ctx.funnelOfferShortCodePublic ?? null,
    };
  }
  const formation = ctx.formations?.find((f) => f.id === formationId);
  if (!formation?.flowId) return null;
  return {
    flowId: formation.flowId,
    creationMode: formation.creationMode ?? null,
    shortCodePublic: formation.shortCodePublic ?? null,
  };
}

function resolveFormationLinkHref(
  formationId: string,
  ctx: LoBlockLinkContext,
  siteSlug: string,
): string {
  const preferAuthenticated = ctx.funnelStepType === 'thank_you';
  const meta = formationMetaFromContext(formationId, ctx);
  const play = resolveFormationPlayHref(meta, { preferAuthenticated });
  if (play) return play;

  const formation = ctx.formations?.find((f) => f.id === formationId);
  if (formation) {
    return creatorFormationHref(
      { id: formation.id, title: formation.title, price: 0, funnelSlug: formation.funnelSlug ?? null },
      siteSlug,
      ctx.customDomainSlug,
    );
  }

  return loPublicSitePath(
    siteSlug,
    { formations: true, formationId },
    ctx.customDomainSlug,
  );
}

export const FUNNEL_STEP_LABELS: Record<FunnelStepType, string> = {
  capture: 'Page capture',
  sales: 'Page de vente',
  checkout: 'Paiement',
  thank_you: 'Page merci',
  custom: 'Page libre',
};

export function buildFunnelLinkContext(
  funnel: {
    slug: string;
    steps?: Array<{ id: string; type: FunnelStepType; slug: string; order: number }>;
    creatorSiteSlug?: string | null;
    storeFormations?: Array<{
      id: string;
      title: string;
      funnelSlug?: string | null;
      flowId?: string | null;
      creationMode?: 'simple' | 'advanced' | null;
      shortCodePublic?: string | null;
    }>;
    resolvedOffer?: {
      formationId?: string;
      flowId?: string;
      creationMode?: 'simple' | 'advanced';
      shortCodePublic?: string | null;
    };
    storeFormation?: { id?: string; flowId?: string; flow?: { creationMode?: string; shortCodePublic?: string | null } };
  },
  step: FunnelStepType | Pick<FunnelStep, 'id' | 'type' | 'slug' | 'order'>,
): LoBlockLinkContext {
  const sorted = funnel.steps?.length ? getSortedSteps({ steps: funnel.steps } as SalesFunnel) : [];
  const currentStep = typeof step === 'object'
    ? step as FunnelStep
    : sorted.find((s) => s.type === step);
  const stepType = currentStep?.type ?? (typeof step === 'string' ? step : 'sales');
  const funnelNextStepHref = currentStep && funnel.steps?.length
    ? getNextFunnelStepPath({ slug: funnel.slug, steps: funnel.steps as FunnelStep[] }, currentStep)
    : undefined;

  const funnelOfferFormationId = funnel.resolvedOffer?.formationId ?? funnel.storeFormation?.id;
  const funnelOfferFlowId = funnel.resolvedOffer?.flowId ?? funnel.storeFormation?.flowId;
  const funnelOfferCreationMode = funnel.resolvedOffer?.creationMode
    ?? (funnel.storeFormation?.flow?.creationMode === 'simple' ? 'simple' : 'advanced');
  const funnelOfferShortCodePublic = funnel.resolvedOffer?.shortCodePublic
    ?? funnel.storeFormation?.flow?.shortCodePublic
    ?? null;

  return {
    mode: 'funnel',
    funnelSlug: funnel.slug,
    funnelStepType: stepType,
    funnelNextStepHref,
    funnelOfferFormationId,
    funnelOfferFlowId,
    funnelOfferCreationMode,
    funnelOfferShortCodePublic,
    siteSlug: funnel.creatorSiteSlug ?? funnel.slug,
    formations: (funnel.storeFormations ?? []).map((f) => ({
      id: f.id,
      title: f.title,
      funnelSlug: f.funnelSlug ?? null,
      flowId: f.flowId ?? null,
      creationMode: f.creationMode ?? null,
      shortCodePublic: f.shortCodePublic ?? null,
    })),
  };
}

export function buildSiteLinkContext(siteSlug: string, customDomainSlug?: string | null): LoBlockLinkContext {
  return { mode: 'site', siteSlug, customDomainSlug };
}

export function parseLoBlockLink(raw: string | undefined): LoBlockLinkTarget {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return { kind: 'funnel-action' };
  if (trimmed === '#cta') return { kind: 'funnel-action' };
  if (trimmed === '#formations') return { kind: 'formations-catalog' };
  if (trimmed.startsWith(FORMATION_PREFIX)) {
    return { kind: 'formation', formationId: trimmed.slice(FORMATION_PREFIX.length) };
  }
  if (trimmed.startsWith(FUNNEL_STEP_PREFIX)) {
    const step = trimmed.slice(FUNNEL_STEP_PREFIX.length) as FunnelStepType;
    if (step in FUNNEL_STEP_LABELS) return { kind: 'funnel-step', funnelStep: step };
  }
  if (isExternalUrl(trimmed)) return { kind: 'external', url: trimmed };
  return { kind: 'external', url: trimmed };
}

export function encodeLoBlockLink(target: LoBlockLinkTarget): string {
  switch (target.kind) {
    case 'funnel-action':
      return '#cta';
    case 'formations-catalog':
      return '#formations';
    case 'formation':
      return target.formationId ? `${FORMATION_PREFIX}${target.formationId}` : '#formations';
    case 'funnel-step':
      return target.funnelStep ? `${FUNNEL_STEP_PREFIX}${target.funnelStep}` : '#cta';
    case 'external':
      return target.url?.trim() || '';
    default:
      return '#cta';
  }
}

export function defaultFunnelActionHref(ctx: LoBlockLinkContext): string {
  const slug = ctx.funnelSlug ?? '';

  if (ctx.funnelStepType === 'capture') {
    return '#funnel-capture-form';
  }

  if (ctx.funnelStepType === 'thank_you') {
    const play = resolveFormationPlayHref(
      ctx.funnelOfferFlowId
        ? {
            flowId: ctx.funnelOfferFlowId,
            creationMode: ctx.funnelOfferCreationMode ?? null,
            shortCodePublic: ctx.funnelOfferShortCodePublic ?? null,
          }
        : null,
      { preferAuthenticated: true },
    );
    if (play) return play;
    return slug ? `/f/${encodeURIComponent(slug)}/merci` : '#';
  }

  if (ctx.funnelNextStepHref) {
    return ctx.funnelNextStepHref;
  }

  switch (ctx.funnelStepType) {
    case 'checkout':
      return slug ? `/f/${encodeURIComponent(slug)}/checkout` : '#';
    case 'custom':
    case 'sales':
    default:
      return slug ? `/f/${encodeURIComponent(slug)}/checkout` : '#';
  }
}

export function resolveLoBlockLink(raw: string | undefined, ctx?: LoBlockLinkContext): string {
  if (!ctx) {
    return resolveLoSiteLink('', raw);
  }

  const parsed = parseLoBlockLink(raw);
  const siteSlug = ctx.siteSlug ?? ctx.funnelSlug ?? '';

  switch (parsed.kind) {
    case 'funnel-action':
      if (ctx.mode === 'funnel') return defaultFunnelActionHref(ctx);
      return resolveLoSiteLink(siteSlug, '#formations', ctx.customDomainSlug);
    case 'funnel-step':
      if (ctx.funnelSlug && parsed.funnelStep) {
        return funnelStepPublicPath(ctx.funnelSlug, parsed.funnelStep);
      }
      return '#';
    case 'formations-catalog':
      return resolveLoSiteLink(siteSlug, '#formations', ctx.customDomainSlug);
    case 'formation': {
      if (!parsed.formationId || !siteSlug) return '#';
      return resolveFormationLinkHref(parsed.formationId, ctx, siteSlug);
    }
    case 'external':
      return parsed.url?.trim() || '#';
    default:
      return '#';
  }
}

export function describeLoBlockLink(raw: string | undefined, ctx?: LoBlockLinkContext): string {
  const parsed = parseLoBlockLink(raw);
  switch (parsed.kind) {
    case 'funnel-action':
      return ctx?.mode === 'funnel' ? 'Action principale de l\'étape' : 'Catalogue formations';
    case 'funnel-step':
      return parsed.funnelStep ? FUNNEL_STEP_LABELS[parsed.funnelStep] : 'Étape funnel';
    case 'formations-catalog':
      return 'Catalogue formations';
    case 'formation': {
      const title = ctx?.formations?.find((f) => f.id === parsed.formationId)?.title;
      return title ? `Formation · ${title}` : 'Formation';
    }
    case 'external':
      return parsed.url || 'URL personnalisée';
    default:
      return raw ?? '';
  }
}
